import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseURL } from "../../config";
import {
  CustomerBalanceSheet,
  PaymentsReceivedSheet,
  SalesSheet,
  CreditNoteDetailsSheet,
  InvoiceDetailSheet,
  PayableDetailsSheet,
  PayableSummarySheet,
  PurchaseByItemSheet,
  ReceivableDetailsSheet,
  ReceivableSummarySheet,
  TimeToGetPaidSheet,
  VendorBalancesSheet,
  VendorBalancesSummarySheet,
  BillDetailsSheet,
  BillPaymentsMadeSheet,
  ExpenseByCategorySheet,
  ExpenseDetailsSheet,
  ExpensesByProjectSheet,
  CustomerBalanceSummarySheet,
  VendorCreditDetailsSheet,
  PurchaseByVendorSheet,
  BillableAmountSheet,
  ExpensesByEmployeesSheet,
  ExpensesByClientSheet,
  AccountTransactionsSheet,
  AccountTypeSummarySheet,
  GeneralLedgerSheet,
  DetailedGeneralLedgerSheet,
  JournalReportSheet,
  TrialBalanceSheet,
  TimesheetProfitabilitySummarySheet,
  ProjectDetailsSheet,
  ProjectSummarySheet,
  ProjectCostSummarySheet,
  ProjectRevenueSummarySheet,
  ProjectsPerformanceSummarySheet,
} from "../../interfaces/reports";

const formatedDate = (date: string) => {
  const currentDate: Date = new Date(date); // Convert the date string to a Date object
  const month: string = ("0" + (currentDate.getMonth() + 1)).slice(-2); // Add leading zero and slice to get last 2 characters
  const day: string = ("0" + currentDate.getDate()).slice(-2); // Add leading zero and slice to get last 2 characters
  const year: number = currentDate.getFullYear();

  // Construct the formatted date string
  const formattedDate: string = `${month}/${day}/${year}`;
  return formattedDate;
};

export const reportsAPI = createApi({
  reducerPath: "/api/reports",
  baseQuery: fetchBaseQuery({ baseUrl: baseURL }), // Set your base URL here
  endpoints: (builder) => ({
    // Define your endpoints here
    getCustomerBalance: builder.query<
      CustomerBalanceSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/customer-balance/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getPaymentsRecieved: builder.query<
      PaymentsReceivedSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/payments-received/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getCreditNoteDetails: builder.query<
      CreditNoteDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/credit-note-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getSales: builder.query<
      SalesSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/sales/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getInvoiceDetails: builder.query<
      InvoiceDetailSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/invoice-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getCustomerBalanceSummary: builder.query<
      CustomerBalanceSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/customer-balance-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getRecievableSummary: builder.query<
      ReceivableSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/receivables-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getTimeToGetPaid: builder.query<
      TimeToGetPaidSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/time-to-get-paid/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getRecievableDetails: builder.query<
      ReceivableDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/receivable-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getVendorBalances: builder.query<
      VendorBalancesSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/vendor-balances/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getVendorBalancesSummary: builder.query<
      VendorBalancesSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/vendor-balances-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getBillDetails: builder.query<
      BillDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/bill-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getBillPaymentsMade: builder.query<
      BillPaymentsMadeSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/bill-payments-made/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getPayableSummary: builder.query<
      PayableSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/payable-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getPayableDetails: builder.query<
      PayableDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/payable-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getPurchaseByItem: builder.query<
      PurchaseByItemSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/purchase-by-item/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getExpenseDetails: builder.query<
      ExpenseDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/expense-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getExpenseByCategory: builder.query<
      ExpenseByCategorySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/expense-by-category/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getExpensesByProject: builder.query<
      ExpensesByProjectSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/expenses-by-project/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getVendorCreditDetails: builder.query<
      VendorCreditDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/vendor-credit-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getPurchaseByVendor: builder.query<
      PurchaseByVendorSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/purchase-by-vendor/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getBillableAmount: builder.query<
      BillableAmountSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/billAble-amount/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getExpensesByEmployees: builder.query<
      ExpensesByEmployeesSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/expenses-by-employee/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getExpensesByClient: builder.query<
      ExpensesByClientSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/expenses-by-client/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getAccountTransactions: builder.query<
      AccountTransactionsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
        accountId: string;
      }
    >({
      query: (data) => ({
        url: `/account-transactions/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
          accountId: `${data.accountId}`,
        },
      }),
    }),
    getAccountTypeSummary: builder.query<
      AccountTypeSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/account-type-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getGeneralLedger: builder.query<
      GeneralLedgerSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
        accountId: string;
      }
    >({
      query: (data) => ({
        url: `/general-ledger/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
          "account-id": `${data.accountId}`,
        },
      }),
    }),
    getDetailedGeneralLedger: builder.query<
      DetailedGeneralLedgerSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
        accountId: string;
      }
    >({
      query: (data) => ({
        url: `/detailed-general-ledger/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
          "account-id": `${data.accountId}`,
        },
      }),
    }),
    getJournalReport: builder.query<
      JournalReportSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/journal-report/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getTrialBalance: builder.query<
      TrialBalanceSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/trial-balance/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getTimesheetProfitabilitySummary: builder.query<
      TimesheetProfitabilitySummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
        projectId: string;
      }
    >({
      query: (data) => ({
        url: `/timesheet-profitability-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
          "project-id": `${data.projectId}`,
        },
      }),
    }),
    getProjectSummary: builder.query<
      ProjectSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/project-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getProjectDetails: builder.query<
      ProjectDetailsSheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
        projectId: string;
      }
    >({
      query: (data) => ({
        url: `/project-details/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
          "project-id": `${data.projectId}`,
        },
      }),
    }),
    getProjectCostSummary: builder.query<
      ProjectCostSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/projects-cost-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getProjectRevenueSummary: builder.query<
      ProjectRevenueSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/projects-revenue-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
    getProjectPerformanceSummary: builder.query<
      ProjectsPerformanceSummarySheet,
      {
        userID: string;
        accessToken: string;
        permission: boolean;
        pageNo: number;
        pageSize: number;
        fromDate: string;
        toDate: string;
      }
    >({
      query: (data) => ({
        url: `/projects-performance-summary/${data.pageNo}/${data.pageSize}/${data.permission}/${data.userID}`,
        method: "GET",
        headers: {
          Authorization: `${data.accessToken}`,
          "from-date": `${formatedDate(data.fromDate)}`,
          "to-date": `${formatedDate(data.toDate)}`,
        },
      }),
    }),
  }),
});

export const {
  useGetCustomerBalanceQuery,
  useGetPaymentsRecievedQuery,
  useGetSalesQuery,
  useGetCreditNoteDetailsQuery,
  useGetInvoiceDetailsQuery,
  useGetCustomerBalanceSummaryQuery,
  useGetRecievableSummaryQuery,
  useGetTimeToGetPaidQuery,
  useGetRecievableDetailsQuery,
  useGetVendorBalancesQuery,
  useGetVendorBalancesSummaryQuery,
  useGetBillDetailsQuery,
  useGetBillPaymentsMadeQuery,
  useGetPayableSummaryQuery,
  useGetPayableDetailsQuery,
  useGetPurchaseByItemQuery,
  useGetExpenseDetailsQuery,
  useGetExpenseByCategoryQuery,
  useGetExpensesByProjectQuery,
  useGetVendorCreditDetailsQuery,
  useGetPurchaseByVendorQuery,
  useGetBillableAmountQuery,
  useGetExpensesByEmployeesQuery,
  useGetExpensesByClientQuery,
  useGetAccountTransactionsQuery,
  useGetAccountTypeSummaryQuery,
  useGetGeneralLedgerQuery,
  useGetDetailedGeneralLedgerQuery,
  useGetJournalReportQuery,
  useGetTrialBalanceQuery,
  useGetTimesheetProfitabilitySummaryQuery,
  useGetProjectSummaryQuery,
  useGetProjectDetailsQuery,
  useGetProjectCostSummaryQuery,
  useGetProjectRevenueSummaryQuery,
  useGetProjectPerformanceSummaryQuery,
} = reportsAPI;
